import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  fetchApprovedFunnelSummaries,
  fetchApprovedFunnelTopics,
  fetchProducttypeNames,
  type ApprovedFunnelsQuery,
} from '@/api/approvedFunnels/api'
import type { ApprovedFunnelTopic } from '@/api/approvedFunnels/types'
import {
  mergeApprovedFunnelNames,
  type ApprovedFunnelRow,
} from '@/features/funnels/approvedFunnelRows'

const SUMMARIES_KEY = 'approved-funnel-summaries' as const
const TOPICS_KEY = 'approved-funnel-topics' as const

export interface ApprovedFunnelPage {
  readonly items: readonly ApprovedFunnelRow[]
  readonly totalCount: number
}

/** Freigegebene Hauptrichter mit aufgelöstem Namen, aus PostgREST. */
export function useApprovedFunnelSummaries(
  query: ApprovedFunnelsQuery,
): UseQueryResult<ApprovedFunnelPage> {
  return useQuery({
    queryKey: [SUMMARIES_KEY, query],
    queryFn: async ({ signal }) => {
      const page = await fetchApprovedFunnelSummaries(query, signal)
      const names = await fetchProducttypeNames(
        page.items.map((item) => item.producttypeKey),
        signal,
      )
      return {
        items: mergeApprovedFunnelNames(page.items, names),
        totalCount: page.totalCount ?? page.items.length,
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

/** Die freigegebenen Themen (Subtrichter) eines aufgeklappten Hauptrichters. */
export function useApprovedFunnelTopics(
  producttypeKey: string,
  enabled: boolean,
): UseQueryResult<readonly ApprovedFunnelTopic[]> {
  return useQuery({
    queryKey: [TOPICS_KEY, producttypeKey],
    queryFn: ({ signal }) => fetchApprovedFunnelTopics(producttypeKey, signal),
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}
